/**
 * Overlay stack: lets stacked overlays agree on who answers Escape.
 *
 * Why this exists: `Modal` and `ConfirmModal` each used to attach their own
 * Escape listener, so with a Modal open and a confirm on top of it ONE Escape
 * closed both: the confirm's "are you sure?" was dismissed and the dialog under
 * it vanished with it, taking the user's unsaved work along.
 *
 * Every open overlay registers here (in the order it opened). A single
 * capture-phase document listener hands Escape to the TOPMOST entry only.
 *
 *   const entry = registerModal(() => onClose());   // when the overlay opens
 *   entry.unregister();                             // when it closes / unmounts
 *   entry.isTop();                                  // is it currently on top?
 *
 * Escape is ignored when something already handled it (`defaultPrevented`) and
 * while an input method editor is composing (`isComposing`, where Escape
 * cancels the composition rather than meaning "close").
 *
 * The listener is attached lazily on the first registration and removed when
 * the stack empties, so a page with no overlay open carries no listener.
 *
 * The page scroll lock lives here too (`lockBodyScroll`), reference counted: with
 * two stacked Modals, closing the top one must not unlock the page while the one
 * beneath is still open.
 */

const stack = [];
let attached = false;

function handleKeyDown(e) {
  if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
  const top = stack[stack.length - 1];
  if (top) top.onEscape(e);
}

function attach() {
  if (attached || typeof document === 'undefined') return;
  // Capture phase: runs before any component-level handler, so a lower overlay
  // can never see an Escape that belongs to the one above it.
  document.addEventListener('keydown', handleKeyDown, true);
  attached = true;
}

function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('keydown', handleKeyDown, true);
  attached = false;
}

/**
 * Put an overlay on top of the stack.
 *
 * @param {Function} onEscape - Called (with the event) when this overlay is on
 *   top and Escape is pressed. A function that does nothing is valid: an overlay
 *   that refuses Escape still SHIELDS the ones under it.
 * @returns {{ isTop: () => boolean, unregister: () => void }}
 */
export function registerModal(onEscape) {
  const entry = { onEscape };
  stack.push(entry);
  attach();
  return {
    isTop: () => stack[stack.length - 1] === entry,
    unregister: () => {
      const i = stack.indexOf(entry);
      if (i !== -1) stack.splice(i, 1);
      if (stack.length === 0) detach();
    },
  };
}

/** Number of overlays currently registered (test and debugging aid). */
export function modalStackSize() {
  return stack.length;
}

let scrollLocks = 0;

/**
 * Lock page scroll while an overlay is open. Reference counted: the body is
 * locked on the first lock and released only when the LAST lock is released, so
 * closing the top of two stacked overlays leaves the page locked for the one
 * still open.
 *
 * @returns {() => void} release function (idempotent)
 */
export function lockBodyScroll() {
  if (typeof document === 'undefined') return () => {};
  scrollLocks += 1;
  document.body.style.overflow = 'hidden';
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLocks -= 1;
    if (scrollLocks === 0) document.body.style.overflow = '';
  };
}
